/**
 * ============================================================
 * Mining Survey WebGIS
 * UI Module
 * ============================================================
 */
import AppState from "../core/app.js";
import LayerManager from "./layers.js";

import proj4 from "proj4";

proj4.defs(
    "EPSG:32650",
    "+proj=utm +zone=50 +datum=WGS84 +units=m +no_defs +type=crs"
);

class UI {
    setupCollapsible(headerId, contentId, toggleId, defaultCollapsed = false) {
        const header = document.getElementById(headerId);
        const content = document.getElementById(contentId);
        const toggle = document.getElementById(toggleId);

        if (!header || !content || !toggle) return;

        if (defaultCollapsed) {
            content.classList.add("collapsed");
            toggle.textContent = "▶";
        } else {
            content.classList.remove("collapsed");
            toggle.textContent = "▼";
        }

        header.setAttribute("aria-expanded", !defaultCollapsed);

        header.addEventListener("click", () => {
            const collapsed = content.classList.toggle("collapsed");

            toggle.textContent = collapsed ? "▶" : "▼";

            header.setAttribute("aria-expanded", !collapsed);
        });
    }

    init() {
        console.log("UI Ready");

        this.setupCollapsible(
            "gpsHeader",
            "gpsContent",
            "gpsToggle",
            false, // terbuka
        );

        this.setupCollapsible(
            "recordHeader",
            "recordContent",
            "recordToggle",
            true, // tertutup
        );
    }

    latLonToUTM50N(lat, lon) {
    const [easting, northing] = proj4(
        "EPSG:4326",
        "EPSG:32650",
        [lon, lat]
    );

    return {
        easting,
        northing
    };
    }

    updatePosition(position) {
        if (!position) return;

        // ============================================
        // WGS84 → UTM Zone 50N
        // ============================================
        const utm = this.latLonToUTM50N(
            position.latitude,
            position.longitude
        );

        // Current Position
        document.getElementById("lat").textContent =
            utm.northing.toFixed(2);

        document.getElementById("lon").textContent =
            utm.easting.toFixed(2);


        // Accuracy
        document.getElementById("acc").textContent =
            position.accuracy.toFixed(1) + " m";


        // Speed
        const speedElement =
            document.getElementById("speed");

        if (speedElement) {
            const speed =
                position.speed
                    ? (position.speed * 3.6).toFixed(1)
                    : "0.0";

            document.getElementById("speed").textContent =
                this.getSpeedText(position.speed);
        }


        // Heading
        const headingElement =
            document.getElementById("heading");

        if (headingElement) {
            document.getElementById("heading").textContent =
                this.getHeadingText(position.heading);
        }


        // Altitude
        const altitudeElement =
            document.getElementById("altitude");

        if (altitudeElement) {
            document.getElementById("altitude").textContent =
                position.altitude == null
                    ? "-"
                    : position.altitude.toFixed(1) + " m";
        }


        // Time
        const timeElement =
            document.getElementById("time");

        if (timeElement) {
            const time =
                new Date(position.timestamp);

            document.getElementById("time").textContent =
                time.toLocaleTimeString();
        }


        // GPS Status
        const status =
            document.getElementById("status");

        const quality =
            this.getGpsQuality(position.accuracy);

        status.textContent =
            `${quality.text} (${position.accuracy.toFixed(1)} m)`;

        status.className =
            "value " + quality.className;


        // Follow button
        const btnFollow =
            document.getElementById("btnFollow");

        if (btnFollow) {
            if (AppState.followMode) {
                btnFollow.classList.add("active");
            } else {
                btnFollow.classList.remove("active");
            }
        }
    }

    getGpsQuality(accuracy) {
        if (accuracy <= 3) {
            return {
                text: "🟢 Excellent",
                className: "gps-excellent",
            };
        }

        if (accuracy <= 10) {
            return {
                text: "🟢 Good",
                className: "gps-good",
            };
        }

        if (accuracy <= 20) {
            return {
                text: "🟡 Fair",
                className: "gps-fair",
            };
        }

        if (accuracy <= 50) {
            return {
                text: "🟠 Poor",
                className: "gps-poor",
            };
        }

        return {
            text: "🔴 Very Poor",
            className: "gps-bad",
        };
    }

    getHeadingText(heading) {
        if (heading === null || heading === undefined) {
            return "-";
        }

        const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

        const index = Math.round(heading / 45) % 8;

        return `${Math.round(heading)}° (${directions[index]})`;
    }

    getSpeedText(speed) {
        const kmh = (speed ?? 0) * 3.6;

        if (kmh < 0.5) {
            return "0.0 km/h (Diam)";
        }

        if (kmh < 7) {
            return `${kmh.toFixed(1)} km/h (Jalan)`;
        }

        if (kmh < 20) {
            return `${kmh.toFixed(1)} km/h (Motor)`;
        }

        return `${kmh.toFixed(1)} km/h (Kendaraan)`;
    }

    updateTrackStatistics(stats) {
        document.getElementById("recDistance").textContent = stats.distance.toFixed(2) + " m";

        document.getElementById("recDuration").textContent = stats.duration;

        document.getElementById("recCurrent").textContent = stats.currentSpeed.toFixed(2) + " km/h";

        document.getElementById("recAverage").textContent = stats.averageSpeed.toFixed(2) + " km/h";

        document.getElementById("recPoints").textContent = stats.points;

        const status = document.getElementById("recStatus");

        if (AppState.track.isRecording) {
            if (AppState.track.isPaused) {
                status.innerHTML = "⏸ Paused";
            } else {
                status.innerHTML = "🔴 Recording";
            }
        } else {
            status.innerHTML = "⚪ Idle";
        }
    }

    enableSaveButton(enable = true) {
        const btn = document.getElementById("btnSave");

        if (!btn) return;

        btn.disabled = !enable;
    }

    showWaypointDialog(defaultName) {
        document.getElementById("waypointDialog").classList.remove("hidden");

        document.getElementById("wpName").value = defaultName;

        document.getElementById("wpDescription").value = "";
    }

    hideWaypointDialog() {
        this.resetWaypointDialog();

        document.getElementById("waypointDialog").classList.add("hidden");
    }

    getWaypointForm() {
        return {
            name: document.getElementById("wpName").value.trim(),

            description: document.getElementById("wpDescription").value.trim(),
        };
    }

    resetWaypointDialog() {
        document.getElementById("wpName").value = "";

        document.getElementById("wpDescription").value = "";
    }

    renderLayerList() {
        const container = document.getElementById("layerList");

        if (!container) return;

        container.innerHTML = "";

        LayerManager.getAll().forEach((layer) => {
            const row = document.createElement("div");

            row.className = "layer-item";

            row.innerHTML = `
            <label class="layer-left">

                <input
                    type="checkbox"
                    class="layer-visible"
                    data-id="${layer.id}"
                    ${layer.visible ? "checked" : ""}>

                <i class="bi bi-map"></i>

                <span class="layer-name">${layer.name}</span>

            </label>

            <div class="layer-actions">

                <button
                    class="layer-zoom"
                    data-id="${layer.id}">

                    <i class="bi bi-crosshair"></i>

                </button>

                <button
                    class="layer-remove"
                    data-id="${layer.id}">

                    <i class="bi bi-trash"></i>

                </button>

            </div>
            `;

            container.appendChild(row);
        });
    }

    showLayerPanel() {
        document.getElementById("layerPanel")?.classList.add("show");

        document.getElementById("panelBackdrop")?.classList.add("show");
    }

    hideLayerPanel() {
        document.getElementById("layerPanel")?.classList.remove("show");

        document.getElementById("panelBackdrop")?.classList.remove("show");
    }
}

export default new UI();
